import Input from "@ui-kit/form/Input/Input.web";
import { useTranslation } from "@shared/utils/internationalization";
import useUpdateEffect from "@shared/hooks/useUpdateEffect/useUpdateEffect";
import {
  forwardRef,
  useCallback,
  useDeferredValue,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { getCardBrandByNumber } from "../CardBrandLogo/utils/getCardBrand";
import {
  CardNumberChangeEvent,
  CardNumberInputProps,
} from "./CardNumberInput.props";
import { luhnCheck } from "./utils/luhn-check";

const CardNumberInput = forwardRef<
  HTMLInputElement | null,
  CardNumberInputProps
>(function CardNumberInput({ onChange }, ref) {
  const { t } = useTranslation("checkout");

  const inputRef = useRef<HTMLInputElement>(null);

  const [cardNumber, setCardNumber] = useState("");
  const [cardBrand, setCardBrand] =
    useState<ReturnType<typeof getCardBrandByNumber>>(null);

  // Defer values for complementary logic such as onChange callbacks
  const deferredCardNumber = useDeferredValue(cardNumber);
  const deferredCardBrand = useDeferredValue(cardBrand);

  const onChangeObjCache = useRef<CardNumberChangeEvent>({
    brand: null,
    complete: false,
    error: null,
  });

  const isInputComplete = useCallback(
    (cardNumber: string) => {
      // If the brand's formatted string length matches the input, consider the input "complete".
      if (deferredCardBrand?.format.length === cardNumber.length) {
        return true;
      }
      return false;
    },
    [deferredCardBrand]
  );

  // Side effect that calls onChange when the card changes
  useUpdateEffect(() => {
    onChangeObjCache.current = {
      ...onChangeObjCache.current,
      brand: deferredCardBrand,
      complete: isInputComplete(deferredCardNumber),
    };

    onChange?.(onChangeObjCache.current);
  }, [onChange, isInputComplete, deferredCardNumber, deferredCardBrand]);

  /**
   * Intercept the input event to replace the resulting value with more legible formatting.
   *
   * The exposed `onChange` API of this component is not a true onChange.
   * It is not passed into the input function. Since "input" events behave like React "onChange" events,
   * we intercept the "input" event, but expose an `onChange` API triggered by an "input" event.
   */
  const interceptOnInput = useCallback((e: Event) => {
    const inputElement = e.target as HTMLInputElement;
    const rawValue = inputElement.value.replace(/\D/g, "");

    // Use the Trie to find the card brand and corresponding format
    const brand = getCardBrandByNumber(rawValue);

    // Default format if no brand is found
    const format = brand ? brand.format : "4444 4444 4444 4444";

    // Apply the format pattern directly
    let formattedValue = "";
    let digitIndex = 0;

    // eslint-disable-next-line no-restricted-syntax
    for (const char of format) {
      if (char === " ") {
        // Add space as specified in the format
        formattedValue += " ";
      } else if (digitIndex < rawValue.length) {
        // Insert the next digit from the input
        formattedValue += rawValue[digitIndex];
        digitIndex += 1;
      }
    }

    setCardBrand(brand);
    setCardNumber(formattedValue.trim());
  }, []);

  /**
   * Validates a card security code based on the `cardNumber` state.
   * Fires an onChange event regardless of the outcome
   */
  const validateInput = useCallback(() => {
    let error: CardNumberChangeEvent["error"] = null;

    if (!luhnCheck(cardNumber)) {
      error = {
        type: "validation_error",
        code: "invalid_number",
        message: t("Your card number is incomplete."),
      };
    }

    onChangeObjCache.current = {
      ...onChangeObjCache.current,
      error,
    };

    onChange?.(onChangeObjCache.current);
  }, [t, onChange, cardNumber, cardBrand]);

  /**
   * Add our change event interceptor via addEventListener.
   * This allows other event listeners to be added without being overridden by an explicity `onChange`
   * React element attribute.
   */
  useEffect(() => {
    inputRef.current?.addEventListener("input", interceptOnInput);

    return () =>
      inputRef.current?.removeEventListener("input", interceptOnInput);
  }, [interceptOnInput]);

  /**
   * Add our blur event interceptor via addEventListener.
   *
   * We use the blur event to trigger our validation
   */
  useEffect(() => {
    inputRef.current?.addEventListener("blur", validateInput);

    return () => inputRef.current?.removeEventListener("blur", validateInput);
  }, [validateInput]);

  /**
   * Expose the internal input ref
   */
  useImperativeHandle<
    HTMLInputElement | null,
    HTMLInputElement | null
  >(ref, () => inputRef.current, []);

  return (
    <Input
      w="100%"
      h="element.3xl"
      lineHeight="1.2em"
      data-testid="card-number-input"
      ref={inputRef}
      placeholder={t("Card number")}
      value={cardNumber}
      readOnly={false}
      variant="unstyled"
      outline="none"
      maxLength={cardBrand?.format.length} // In the rare case a card number could not be recognized, a limit does not get set. On mount, this is also undefined.
      // @see https://web.dev/learn/forms/payment#help_users_enter_their_payment_details
      type="text"
      inputMode="numeric"
      autoComplete="cc-number"
    />
  );
});

export default CardNumberInput;
